// The hull helm's held controls, on the clock a crew's hand actually lives on:
// the drive lever that W/S (or a pad's helm) walks, and the SPD readout that
// arms HULL BLOCKED. Split out of main.js's frame loop so the clock they run
// on can be pinned without a browser (client/test/helm.test.mjs).
//
// frame() clamps its step so one hitch can't fling the integrators
// (prediction, cameras, particles). A control the player HOLDS can't ride that
// clamp. The drive lever once walked on it, so under 10 fps it ran in slow
// motion: at the 1-2 fps a software renderer draws the steppe, ten seconds of
// S from flank crept it to 0.19, barely astern, and a tank grinding a treeline
// sat there under the very banner telling it to back up. The SPD readout had
// the same clamp: a hull stalled on a treeline held "68 km/h" for half a
// minute, and HULL BLOCKED (fed by that number) stayed dark through the whole
// stall. And a GPU drawing the steppe at 120 fps never sees any of it, so no
// browser run on a fast machine can tell the two clocks apart.
//
// That is why the helm takes the frame's CLOCKS (frameClocks), not a number:
// which clock a held control walks on is this file's decision, not the call
// site's, and the tests fly it at 120 fps and at one frame every three
// seconds.

// frame()'s clamp on the integrators' step (seconds): one hitch can't fling
// the prediction, the cameras or the particles past where a frame could.
export const FRAME_DT_MAX = 0.1;

/** One rendered frame's two clocks: `wallDt`, how long the frame REALLY took
 *  (seconds, never capped), and `dt`, the same clamped to FRAME_DT_MAX for the
 *  integrators. Everything the frame integrates reads `dt`; a control the
 *  player holds and a rate read off the world read `wallDt`. */
export function frameClocks(wallDt) {
  return { wallDt, dt: Math.min(wallDt, FRAME_DT_MAX) };
}

// How fast W/S walk the hull drive lever: DRIVE_LEVER_RATE of travel for
// every wall-clock second the key is HELD — flank to full astern in ~1.1 s,
// which is the whole HULL BLOCKED recovery ("BACK UP (S)").
export const DRIVE_LEVER_RATE = 0.9;

/** The drive lever (0..1; a land hull's bottom quarter is reverse) after one
 *  frame of a held order, driveAxis: +1 ahead, -1 astern — W/S, or a pad's
 *  helm, which puts the same ±1 there. It walks on the WALL clock and stops at
 *  its stops. No cap on the step: frames of two and three seconds happen on a
 *  starved machine, and any cap short of them brings the slow motion back.
 *  The worst a hitch can do is credit a key pressed inside it with the rest
 *  of the hitch — one frame of quantization, and the lever's stops bound it. */
export function walkDriveLever(throttle, driveAxis, clocks) {
  const v = throttle + driveAxis * clocks.wallDt * DRIVE_LEVER_RATE;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** The hull's SPD readout (m/s) after one frame: the metres its anchor
 *  covered (distM) over the seconds the frame REALLY took, clamped to capMS
 *  (a shade over flank: the raw track speed spikes to impossible values on
 *  snapshot position jumps, 130-170 km/h on an 18 m/s hull), and eased toward
 *  on seconds, not frames, so the readout stays honest and steady. On the
 *  clamped dt a 3 s frame read a 6.7 m/s reverse as flank, and a stalled
 *  hull's speed bled off a tenth of a second per frame: HULL BLOCKED lit only
 *  once the tank was already backing out. */
export function readHullSpeed(smooth, distM, clocks, capMS) {
  const raw = Math.min(distM / Math.max(clocks.wallDt, 1e-3), capMS);
  return smooth + (raw - smooth) * (1 - Math.exp(-4 * clocks.wallDt));
}

// --- the land hull's neutral notch -------------------------------------------
//
// A land hull's lever has a gear in it: the bottom quarter is astern, the rest
// ahead, and the quarter mark between them is neutral. Neutral used to be a
// single point, and no hand ever finds a point: W/S walk the lever 0.015 a
// frame at 60 Hz, a thumb on the slider is coarser, and a tank let off the gas
// sat there crawling a few tenths of a metre a second, ahead or astern, for
// as long as the crew sat in it (flightsim-ak1). So the lever has a notch, on
// both ends of the wire: the server reads anything inside it as a dead stop
// (landThrottle), and the keys catch in it on the way through.

// Where neutral sits on the 0..1 lever: the top of the reverse band.
export const LAND_NEUTRAL = 0.25;

// Half-width of the neutral notch in lever travel — MUST match the server's
// landNeutralNotch. Wide enough to swallow a key let go a couple of frames
// late or a thumb a few pixels off; a sliver of the lever all the same.
export const LAND_NEUTRAL_NOTCH = 0.04;

// How long a held W or S rests in the notch when it walks the lever through
// neutral (wall-clock seconds) before it carries on into the other gear. Long
// enough that "ease off to a stop" is a key released any time in the third
// of a second after the lever lands, rather than inside the ~90 ms it takes to
// walk across the notch; short enough that BACK UP (S) from flank still
// reaches full astern inside a second and a half.
export const NEUTRAL_DWELL_SEC = 0.35;

/** The land drive order (-0.4 full astern .. 1 flank) for a 0..1 lever: the
 *  client's mirror of the server's landThrottle, notch and all. Anything in
 *  the notch is exactly 0; each gear rescales from the notch's edge. */
export function landDrive(thr) {
  const t = thr < 0 ? 0 : thr > 1 ? 1 : thr;
  const lo = LAND_NEUTRAL - LAND_NEUTRAL_NOTCH;
  const hi = LAND_NEUTRAL + LAND_NEUTRAL_NOTCH;
  if (t < lo) return (-(lo - t) / lo) * 0.4;
  if (t > hi) return (t - hi) / (1 - hi);
  return 0;
}

/** A fresh notch for walkLandLever: one per crewed hull is plenty. */
export function newNeutralNotch() {
  return { caught: false, held: 0 };
}

/** walkDriveLever for a land hull's geared lever. A held order that walks the
 *  lever INTO neutral catches there for NEUTRAL_DWELL_SEC of further holding
 *  before carrying on; letting go inside the notch parks the lever on neutral
 *  exactly, so the HUD reads what the hull does. A press that STARTS on
 *  neutral moves off at once — the catch is for stopping, not for starting.
 *  All of it in wall-clock time (the rest of the frame after the lever lands
 *  pays the dwell, and what's left of it walks on), so it reads the same at
 *  120 fps and at one frame every three seconds. `notch` is the per-hull state
 *  from newNeutralNotch(). */
export function walkLandLever(throttle, driveAxis, clocks, notch) {
  if (driveAxis === 0) {
    notch.caught = false;
    notch.held = 0;
    return Math.abs(throttle - LAND_NEUTRAL) <= LAND_NEUTRAL_NOTCH ? LAND_NEUTRAL : throttle;
  }
  const dir = driveAxis > 0 ? 1 : -1;
  const rate = Math.abs(driveAxis) * DRIVE_LEVER_RATE;
  let t = clocks.wallDt;
  let v = throttle;
  if (v !== LAND_NEUTRAL) {
    const gap = (LAND_NEUTRAL - v) * dir; // lever travel to neutral, if ahead of us
    if (!(gap > 0 && gap <= rate * t)) return walkDriveLever(v, driveAxis, clocks);
    t -= gap / rate;
    v = LAND_NEUTRAL;
    notch.caught = true;
    notch.held = 0;
  }
  if (notch.caught) {
    const wait = Math.min(t, NEUTRAL_DWELL_SEC - notch.held);
    notch.held += wait;
    t -= wait;
    if (notch.held < NEUTRAL_DWELL_SEC) return LAND_NEUTRAL;
    notch.caught = false;
  }
  return walkDriveLever(v, driveAxis, frameClocks(t));
}
